
import React from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@radix-ui/react-popover";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import { DateRange } from "react-day-picker";

interface DateSelectorProps {
  startDate: Date | undefined;
  endDate: Date | undefined;
  onStartDateChange: (date: Date | undefined) => void;
  onEndDateChange: (date: Date | undefined) => void;
}

export const DateSelector: React.FC<DateSelectorProps> = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
}) => {
  const [dateRange, setDateRange] = React.useState<DateRange | undefined>({
    from: startDate,
    to: endDate,
  });

  const handleRangeSelect = (range: DateRange | undefined) => {
    setDateRange(range);
    onStartDateChange(range?.from);
    onEndDateChange(range?.to);
  };

  const formatDateRange = () => {
    if (!dateRange?.from) {
      return "Sélectionner les dates";
    }
    
    if (dateRange.from && !dateRange.to) {
      return `Du ${format(dateRange.from, "dd/MM/yyyy", { locale: fr })} - Sélectionnez la date de fin`;
    }
    
    if (dateRange.from && dateRange.to) {
      return `Du ${format(dateRange.from, "dd/MM/yyyy", { locale: fr })} au ${format(dateRange.to, "dd/MM/yyyy", { locale: fr })}`;
    }
    
    return "Sélectionner les dates";
  };

  React.useEffect(() => {
    setDateRange({
      from: startDate,
      to: endDate,
    });
  }, [startDate, endDate]);

  return (
    <div className="bg-white rounded-lg shadow-sm p-4">
      <Label className="block text-[#333333] font-bold mb-2">
        Période d'assignation
      </Label>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={`w-full justify-start text-left font-normal ${
              !dateRange?.from ? "text-muted-foreground" : ""
            }`}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {formatDateRange()}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 bg-white border rounded-lg shadow-lg z-50" align="start">
          <Calendar
            mode="range"
            selected={dateRange}
            onSelect={handleRangeSelect}
            numberOfMonths={2}
            initialFocus
            className="p-3 pointer-events-auto"
            locale={fr}
            weekStartsOn={1}
            disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
          />
          <div className="p-3 border-t bg-gray-50 text-sm text-gray-600">
            💡 Sélectionnez une date de début, puis une date de fin pour définir la période d'assignation
          </div>
        </PopoverContent>
      </Popover>
      
      {/* Summary display */}
      {dateRange?.from && dateRange?.to && (
        <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <div className="text-sm text-blue-800">
            <strong>Période sélectionnée :</strong>
          </div>
          <div className="text-sm text-blue-700 mt-1">
            Du {format(dateRange.from, "EEEE dd MMMM yyyy", { locale: fr })} au {format(dateRange.to, "EEEE dd MMMM yyyy", { locale: fr })}
          </div>
          <div className="text-xs text-blue-600 mt-1">
            {Math.ceil((dateRange.to.getTime() - dateRange.from.getTime()) / (1000 * 60 * 60 * 24)) + 1} jour(s) d'assignation
          </div>
        </div>
      )}
    </div>
  );
};
